import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Spinner, Tabs, Tab, Modal, Dropdown, Collapse } from 'react-bootstrap';
import { setupsApi } from '../services/api';

const TIER_ORDER = ['DIAMOND', 'PLATINUM', 'GOLD', 'SILVER', 'FADED'];
const TIER_EMOJI = { DIAMOND: '💎', PLATINUM: '🪙', GOLD: '🥇', SILVER: '🥈', FADED: '⬜' };

// ─── Helpers ──────────────────────────────────────────────────────────────────

function timeAgo(timestamp) {
  const diff = Date.now() - new Date(timestamp).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min${mins === 1 ? '' : 's'} ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hr${hrs === 1 ? '' : 's'} ago`;
  const days = Math.floor(hrs / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
}

const TF_TO_INTERVAL = { '1h': '60', '4h': '240', 'D': 'D', 'W': 'W' };

function getAnchorTimeframe(setup) {
  if (setup.type === 'pullback_pair') return setup.timeframe;
  if (setup.type === 'diamond') {
    // anchor_timestamp may reflect a chain signal newer than all pairs — check chain first
    const chainMatch = (setup.chain_signals || []).find(s => s.timestamp === setup.anchor_timestamp);
    if (chainMatch) return chainMatch.timeframe;
    return setup.pullback_pairs?.[0]?.timeframe || 'D'; // [0] is now sorted to most recent
  }
  // chain: match anchor_timestamp against chain_signals to get the exact timeframe
  const match = (setup.chain_signals || []).find(s => s.timestamp === setup.anchor_timestamp);
  if (match) return match.timeframe;
  return 'D';
}

function getTradingViewUrl(setup) {
  const symbol = setup.symbol.toUpperCase();
  const tf = getAnchorTimeframe(setup);
  const interval = TF_TO_INTERVAL[tf] || tf;
  return `https://www.tradingview.com/chart/?symbol=${symbol}&interval=${interval}`;
}

function formatTs(ts) {
  if (!ts) return '—';
  return new Date(ts).toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: false
  });
}

function formatAnchorTime(ts) {
  if (!ts) return '—';
  return `${timeAgo(ts)} · ${formatTs(ts)}`;
}

function formatPrice(price) {
  if (price == null) return '—';
  const n = parseFloat(price);
  if (isNaN(n)) return '—';
  if (n < 10) return n.toFixed(5);
  if (n < 1000) return n.toFixed(2);
  return n.toLocaleString('en-US', { maximumFractionDigits: 0 });
}

// ─── Freshness bar (wifi-style, 4 segments) ───────────────────────────────────
const FreshnessBar = ({ candles_elapsed = 0 }) => {
  const bars =
    candles_elapsed <= 5  ? 4 :
    candles_elapsed <= 10 ? 3 :
    candles_elapsed <= 15 ? 2 :
    candles_elapsed <= 20 ? 1 : 0;

  return (
    <div className="freshness-bars">
      {[1, 2, 3, 4].map(i => (
        <div
          key={i}
          className={`freshness-bar-segment ${i <= bars ? 'active' : ''}`}
          style={{ height: `${i * 5}px` }}
        />
      ))}
      <span className="candles-label">{candles_elapsed}c</span>
    </div>
  );
};

// ─── Setup Guide ──────────────────────────────────────────────────────────────
const SetupGuide = ({ show }) => (
  <Collapse in={show}>
    <div>
      <div className="setup-guide-panel">
        <div className="setup-guide-section">
          <div className="setup-guide-section-title">Tiers</div>
          <div className="setup-guide-rows">
            <div className="setup-guide-row">
              <span className="setup-guide-icon">💎</span>
              <span className="setup-guide-name">Diamond</span>
              <span className="setup-guide-desc">Multiple aligned timeframes with pullback + strong chain (≥1.75x). Rarest, highest conviction.</span>
            </div>
            <div className="setup-guide-row">
              <span className="setup-guide-icon">🪙</span>
              <span className="setup-guide-name">Platinum</span>
              <span className="setup-guide-desc">Single pullback with strong chain (≥1.75x), direction aligned.</span>
            </div>
            <div className="setup-guide-row">
              <span className="setup-guide-icon">🥇</span>
              <span className="setup-guide-name">Gold</span>
              <span className="setup-guide-desc">Pullback with any aligned chain, OR a strong standalone chain (≥1.75x) with no pullback required.</span>
            </div>
            <div className="setup-guide-row">
              <span className="setup-guide-icon">🥈</span>
              <span className="setup-guide-name">Silver</span>
              <span className="setup-guide-desc">Chain or pullback present but below Gold criteria — direction unknown, mismatched, or weight insufficient.</span>
            </div>
          </div>
        </div>

        <div className="setup-guide-section">
          <div className="setup-guide-section-title">Tags &amp; Reference</div>
          <div className="setup-guide-rows">
            <div className="setup-guide-row">
              <span className="setup-guide-icon">↩</span>
              <span className="setup-guide-name">Pullback</span>
              <span className="setup-guide-desc">A pullback signal has formed, paired with a directional signal on the same timeframe.</span>
            </div>
            <div className="setup-guide-row">
              <span className="setup-guide-icon">💎</span>
              <span className="setup-guide-name">Multi-TF</span>
              <span className="setup-guide-desc">Multiple timeframes have pullback pairs — the Diamond component.</span>
            </div>
            <div className="setup-guide-row">
              <span className="setup-guide-icon">⚠️</span>
              <span className="setup-guide-name">Conflicted</span>
              <span className="setup-guide-desc">The pullback direction opposes the chain direction. Treat with caution.</span>
            </div>
            <div className="setup-guide-row">
              <span className="setup-guide-icon">⚠️</span>
              <span className="setup-guide-name">Pullback Fired First</span>
              <span className="setup-guide-desc">The pullback signal preceded the directional signal. Atypical sequence — treat with caution.</span>
            </div>
            <div className="setup-guide-row">
              <span className="setup-guide-icon">🔘</span>
              <span className="setup-guide-name">Stale</span>
              <span className="setup-guide-desc">One or more signals in this setup are older than expected for the timeframe (anchor &gt;20 candles, or non-anchor &gt;50 candles).</span>
            </div>
            <div className="setup-guide-row">
              <span className="setup-guide-icon">⚖️</span>
              <span className="setup-guide-name">Chain Weights</span>
              <span className="setup-guide-desc">Chain weights reflect timeframe significance: 1h=0.25x, 4h=0.5x, D=1x, W=2x. A W+D alignment (3x) outweighs four 1h signals combined.</span>
            </div>
            <div className="setup-guide-row">
              <span className="setup-guide-icon">⬜</span>
              <span className="setup-guide-name">Faded</span>
              <span className="setup-guide-desc">Stale anchor (signal older than 20 candles for its timeframe) or no chain data found. Low relevance.</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Collapse>
);

// ─── Symbol Filter ─────────────────────────────────────────────────────────────
const SymbolFilter = ({ allSymbols, selectedSymbols, onChange }) => {
  const isAll = selectedSymbols.size === 0;
  const label = isAll
    ? 'All Symbols'
    : selectedSymbols.size === 1
    ? [...selectedSymbols][0]
    : `${selectedSymbols.size} symbols`;

  const toggle = (sym) => {
    const next = new Set(selectedSymbols);
    if (next.has(sym)) next.delete(sym); else next.add(sym);
    onChange(next);
  };

  return (
    <Dropdown autoClose="outside" className="symbol-filter-dropdown">
      <Dropdown.Toggle variant="outline-secondary" size="sm" className="symbol-filter-toggle">
        {label}
      </Dropdown.Toggle>
      <Dropdown.Menu className="symbol-filter-menu">
        <Dropdown.Item onClick={() => onChange(new Set())} className={isAll ? 'symbol-filter-item-active' : ''}>
          All Symbols
        </Dropdown.Item>
        <Dropdown.Divider />
        {allSymbols.map(sym => (
          <Dropdown.Item key={sym} onClick={() => toggle(sym)} className={selectedSymbols.has(sym) ? 'symbol-filter-item-active' : ''}>
            <span className="symbol-filter-check">{selectedSymbols.has(sym) ? '✓' : '\u00a0\u00a0'}</span>
            {sym}
          </Dropdown.Item>
        ))}
      </Dropdown.Menu>
    </Dropdown>
  );
};

// ─── Setup Modal ──────────────────────────────────────────────────────────────
const SetupModal = ({ setup, onHide }) => {
  if (!setup) return null;

  const isUp = setup.direction === 'up';
  const tvUrl = getTradingViewUrl(setup);

  const isAnchorTs = ts =>
    ts && setup.anchor_timestamp &&
    new Date(ts).getTime() === new Date(setup.anchor_timestamp).getTime();

  const chainSignals = setup.chain_signals || [];

  // For diamond: pairs come from pullback_pairs; for pullback_pair: wrap self
  const pairs = setup.type === 'diamond'
    ? setup.pullback_pairs || []
    : setup.type === 'pullback_pair'
    ? [setup]
    : [];

  // ─── Chain split logic ────────────────────────────────────────────────────
  const upTfs = setup.chain_timeframes_up || [];
  const downTfs = setup.chain_timeframes_down || [];
  const longSigs = chainSignals.filter(s => upTfs.includes(s.timeframe));
  const shortSigs = chainSignals.filter(s => downTfs.includes(s.timeframe));
  const showLongChain = upTfs.length >= 2;
  const showShortChain = downTfs.length >= 2;
  const otherSigs = [
    ...(upTfs.length === 1 ? longSigs : []),
    ...(downTfs.length === 1 ? shortSigs : []),
    ...chainSignals.filter(s => !upTfs.includes(s.timeframe) && !downTfs.includes(s.timeframe)),
  ];
  const longLatest = longSigs.length > 0 ? Math.max(...longSigs.map(s => new Date(s.timestamp).getTime())) : 0;
  const shortLatest = shortSigs.length > 0 ? Math.max(...shortSigs.map(s => new Date(s.timestamp).getTime())) : 0;
  const longFirst = !showShortChain || (showLongChain && longLatest >= shortLatest);

  const renderChainRow = (sig, i) => (
    <div
      key={i}
      className={`modal-signal-row ${isAnchorTs(sig.timestamp) ? 'modal-signal-anchor' : ''}`}
    >
      <span className="anchor-tf-pill">{sig.timeframe}</span>
      <span className={`modal-sig-type ${sig.signal === 'long' ? 'modal-sig-long' : 'modal-sig-short'}`}>
        {sig.signal}
      </span>
      <span className="modal-sig-time">{formatTs(sig.timestamp)}</span>
      <span className="modal-sig-price">{formatPrice(sig.price)}</span>
      <span className="modal-sig-weight">{sig.weight}x</span>
      {isAnchorTs(sig.timestamp) && <span className="anchor-label">ANCHOR</span>}
    </div>
  );

  const LongChainSection = showLongChain ? (
    <div className="modal-chain-subsection">
      <div className="modal-chain-header modal-chain-long">
        Long Chain · {setup.chain_weight_up}x↑
      </div>
      <div className="modal-signal-list">{longSigs.map(renderChainRow)}</div>
    </div>
  ) : null;

  const ShortChainSection = showShortChain ? (
    <div className="modal-chain-subsection">
      <div className="modal-chain-header modal-chain-short">
        Short Chain · {setup.chain_weight_down}x↓
      </div>
      <div className="modal-signal-list">{shortSigs.map(renderChainRow)}</div>
    </div>
  ) : null;

  const OtherSection = otherSigs.length > 0 ? (
    <div className="modal-chain-subsection">
      <div className="modal-chain-header">Other Signals</div>
      <div className="modal-signal-list">{otherSigs.map(renderChainRow)}</div>
    </div>
  ) : null;

  return (
    <Modal show onHide={onHide} size="lg" centered className="setup-modal">
      <Modal.Header closeButton className="setup-modal-header">
        <Modal.Title>
          <div className="setup-modal-title">
            <a href={tvUrl} target="_blank" rel="noopener noreferrer" className="setup-modal-symbol">
              {setup.symbol.toUpperCase()}
            </a>
            <span className={isUp ? 'direction-up' : 'direction-down'}>
              {isUp ? '↑' : '↓'}
            </span>
            <span className="tier-badge">{TIER_EMOJI[setup.tier]} {setup.tier}</span>
            {setup.conflicted && <span className="conflicted-badge">⚠ CONFLICTED</span>}
          </div>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="setup-modal-body">

        {/* Chain section */}
        {chainSignals.length > 0 && (
          <div className="modal-section">
            <div className="modal-section-title">⛓ Chain Analysis</div>
            {longFirst ? <>{LongChainSection}{ShortChainSection}</> : <>{ShortChainSection}{LongChainSection}</>}
            {OtherSection}
          </div>
        )}

        {/* Pullback pairs section */}
        {pairs.length > 0 && (
          <div className="modal-section">
            <div className="modal-section-title">
              ↩ Pullback {setup.type === 'diamond' ? 'Pairs' : 'Setup'}
            </div>
            {pairs.map((pair, pi) => {
              const pairSignals = pair.signals || [];
              return (
                <div key={pi} className="modal-pair">
                  {setup.type === 'diamond' && (
                    <div className="modal-pair-header">
                      <span className="anchor-tf-pill">{pair.timeframe}</span>
                      {pair.pullback_led && (
                        <span className="setup-tag tag-pullback-led">⚠ Pullback Fired First</span>
                      )}
                    </div>
                  )}
                  {pairSignals.length > 0 ? (
                    <div className="modal-signal-list">
                      {pairSignals.map((sig, si) => (
                        <div
                          key={si}
                          className={`modal-signal-row ${sig.isAnchor ? 'modal-signal-anchor' : ''}`}
                        >
                          <span className="anchor-tf-pill">{sig.timeframe}</span>
                          <span className="modal-sig-type">{sig.signal}</span>
                          <span className="modal-sig-time">{formatTs(sig.timestamp)}</span>
                          <span className="modal-sig-price">{formatPrice(sig.price)}</span>
                          {sig.isAnchor && (setup.type !== 'diamond' || isAnchorTs(sig.timestamp)) && (
                            <span className="anchor-label">ANCHOR</span>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="modal-no-data">Signal detail not available</div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {chainSignals.length === 0 && pairs.length === 0 && (
          <div className="modal-no-data">No detailed signal data available</div>
        )}

      </Modal.Body>
    </Modal>
  );
};

// ─── Setup Card ───────────────────────────────────────────────────────────────
const getTimeframeDisplay = (setup) => {
  const tfs = setup.direction === 'up' ? setup.chain_timeframes_up : setup.chain_timeframes_down;
  return (tfs || []).join(' + ');
};

const SetupCard = ({ setup, onSelect }) => {
  const tierClass = `setup-tier-${setup.tier.toLowerCase()}`;
  const isUp = setup.direction === 'up';
  const timeframeDisplay = getTimeframeDisplay(setup);
  const anchorTf = getAnchorTimeframe(setup);
  const tvUrl = getTradingViewUrl(setup);
  const isStale = setup.stale || setup.stale_pair;

  return (
    <div
      className={`setup-card ${tierClass} ${isStale ? 'setup-stale' : ''}`}
      onClick={() => onSelect(setup)}
      style={{ cursor: 'pointer' }}
    >
      <div className="setup-card-header">
        <div className="setup-card-badges">
          <span className="tier-badge">
            {TIER_EMOJI[setup.tier]} {setup.tier}
          </span>
          {isStale && <span className="stale-badge">STALE</span>}
          {setup.conflicted && (
            <span className="conflicted-badge">⚠ CONFLICTED</span>
          )}
        </div>
        <FreshnessBar candles_elapsed={setup.candles_elapsed} />
      </div>

      <div className="setup-card-body">
        <div className="setup-symbol">
          <a
            href={tvUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="setup-symbol-link"
            onClick={e => e.stopPropagation()}
          >
            {setup.symbol.toUpperCase()}
          </a>
          <span className={isUp ? 'direction-up' : 'direction-down'}>
            {isUp ? '↑' : '↓'}
          </span>
          <span className="anchor-tf-pill anchor-tf-pill-symbol">{anchorTf}</span>
        </div>

        <div className="setup-anchor-meta">
          <span className="anchor-time">{formatAnchorTime(setup.anchor_timestamp)}</span>
        </div>

        <div className="setup-weights">
          {isUp ? (
            <>
              {setup.chain_weight_up > 0 && <span className="chain-badge chain-badge-up">{setup.chain_weight_up}x↑</span>}
              {setup.chain_weight_down > 0 && <span className="chain-badge chain-badge-down">{setup.chain_weight_down}x↓</span>}
            </>
          ) : (
            <>
              {setup.chain_weight_down > 0 && <span className="chain-badge chain-badge-down">{setup.chain_weight_down}x↓</span>}
              {setup.chain_weight_up > 0 && <span className="chain-badge chain-badge-up">{setup.chain_weight_up}x↑</span>}
            </>
          )}
          {setup.type === 'pullback_pair' && (
            <span className={`chain-badge pullback-badge pullback-badge-${isUp ? 'up' : 'down'}`}>
              {isUp ? '↑' : '↓'} Pullback · {setup.timeframe}
            </span>
          )}
          {setup.type === 'diamond' && (setup.pullback_pairs || []).length > 0 && (
            <span className={`chain-badge pullback-badge pullback-badge-${isUp ? 'up' : 'down'}`}>
              {isUp ? '↑' : '↓'} Pullback · {(setup.pullback_pairs || []).map(p => p.timeframe).join(' · ')}
            </span>
          )}
        </div>

        {timeframeDisplay && (
          <div className="setup-timeframes">{timeframeDisplay}</div>
        )}

        <div className="setup-tags">
          {setup.type === 'diamond' && <span className="setup-tag">💎 Multi-TF</span>}
          {setup.pullback_led && (
            <span className="setup-tag tag-pullback-led">⚠ Pullback Fired First</span>
          )}
        </div>
      </div>
      <div
        className={`setup-card-watermark setup-card-watermark-${isUp ? 'up' : 'down'}`}
        aria-hidden="true"
      />
    </div>
  );
};

// ─── Chain Card (Chains tab raw view) ────────────────────────────────────────
const ChainCard = ({ setup, onSelect }) => {
  const anchorTf = getAnchorTimeframe(setup);
  const tvUrl = `https://www.tradingview.com/chart/?symbol=${setup.symbol.toUpperCase()}&interval=${TF_TO_INTERVAL[anchorTf] || anchorTf}`;
  const isStale = setup.stale || setup.stale_pair;
  return (
  <div className={`chain-raw-card ${isStale ? 'setup-stale' : ''}`} onClick={() => onSelect(setup)} style={{ cursor: 'pointer' }}>
    <div className="chain-raw-header">
      <div className="setup-symbol">
        <a
          href={tvUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="setup-symbol-link"
          onClick={e => e.stopPropagation()}
        >
          {setup.symbol.toUpperCase()}
        </a>
        {setup.direction && (
          <span className={setup.direction === 'up' ? 'direction-up' : 'direction-down'}>
            {setup.direction === 'up' ? '↑' : '↓'}
          </span>
        )}
      </div>
      <div className="chain-raw-badges">
        {isStale && <span className="stale-badge">STALE</span>}
        <FreshnessBar candles_elapsed={setup.candles_elapsed} />
      </div>
    </div>
    <div className="setup-anchor-meta">
      <span className="anchor-tf-pill">{getAnchorTimeframe(setup)}</span>
      <span className="anchor-time">{formatAnchorTime(setup.anchor_timestamp)}</span>
    </div>
    <div className="setup-weights">
      {setup.chain_weight_up >= setup.chain_weight_down ? (
        <>
          {setup.chain_weight_up > 0 && <span className="chain-badge chain-badge-up">{setup.chain_weight_up}x↑</span>}
          {setup.chain_weight_down > 0 && <span className="chain-badge chain-badge-down">{setup.chain_weight_down}x↓</span>}
        </>
      ) : (
        <>
          {setup.chain_weight_down > 0 && <span className="chain-badge chain-badge-down">{setup.chain_weight_down}x↓</span>}
          {setup.chain_weight_up > 0 && <span className="chain-badge chain-badge-up">{setup.chain_weight_up}x↑</span>}
        </>
      )}
    </div>
    <div className="setup-timeframes">
      {setup.chain_timeframes_up?.length > 0 && <span>↑ {setup.chain_timeframes_up.join(' + ')}</span>}
      {setup.chain_timeframes_up?.length > 0 && setup.chain_timeframes_down?.length > 0 && ' · '}
      {setup.chain_timeframes_down?.length > 0 && <span>↓ {setup.chain_timeframes_down.join(' + ')}</span>}
    </div>
  </div>
  );
};

// ─── Pullback Card (Pullbacks tab raw view) ───────────────────────────────────
const PullbackCard = ({ pair, onSelect }) => {
  const isUp = pair.direction === 'up';
  const pairSignals = pair.signals || [];
  const interval = TF_TO_INTERVAL[pair.timeframe] || pair.timeframe;
  const tvUrl = `https://www.tradingview.com/chart/?symbol=${pair.symbol.toUpperCase()}&interval=${interval}`;
  const isStale = pair.stale || pair.stale_pair;
  return (
    <div
      className={`pullback-raw-card ${isStale ? 'setup-stale' : ''}`}
      onClick={() => onSelect(pair)}
      style={{ cursor: 'pointer' }}
    >
      <div className="pullback-raw-header">
        <div className="pullback-raw-title">
          <span className="setup-symbol" style={{ fontSize: '1.1rem' }}>
            <a
              href={tvUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="setup-symbol-link"
              onClick={e => e.stopPropagation()}
            >
              {pair.symbol.toUpperCase()}
            </a>
            <span className={isUp ? 'direction-up' : 'direction-down'}>
              {isUp ? '↑' : '↓'}
            </span>
          </span>
          <span className="anchor-tf-pill">{pair.timeframe}</span>
          {isStale && <span className="stale-badge">STALE</span>}
          {pair.pullback_led && (
            <span className="setup-tag tag-pullback-led">⚠ Pullback Fired First</span>
          )}
        </div>
        <FreshnessBar candles_elapsed={pair.candles_elapsed} />
      </div>
      <div className="anchor-time" style={{ fontSize: '0.72rem', marginBottom: '0.4rem' }}>
        {formatAnchorTime(pair.anchor_timestamp)}
      </div>
      <div className="pullback-raw-signals">
        {pairSignals.length > 0 ? pairSignals.map((sig, i) => (
          <div
            key={i}
            className={`modal-signal-row ${sig.isAnchor ? 'modal-signal-anchor' : ''}`}
          >
            <span className="modal-sig-type">{sig.signal}</span>
            <span className="modal-sig-time">{formatTs(sig.timestamp)}</span>
            <span className="modal-sig-price">{formatPrice(sig.price)}</span>
            {sig.isAnchor && <span className="anchor-label">ANCHOR</span>}
          </div>
        )) : (
          <div className="modal-no-data">Signal detail not available</div>
        )}
      </div>
    </div>
  );
};

// ─── Empty states ─────────────────────────────────────────────────────────────
const EmptyState = ({ message }) => (
  <div className="setup-empty-state">
    <i className="bi bi-search text-muted" style={{ fontSize: '2rem' }}></i>
    <p className="mt-3 text-muted">{message}</p>
  </div>
);

// ─── Page ─────────────────────────────────────────────────────────────────────
const SetupsPage = () => {
  const [setups, setSetups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('setups');
  const [lastUpdated, setLastUpdated] = useState(null);
  const [sortMode, setSortMode] = useState('recency');
  const [selectedSetup, setSelectedSetup] = useState(null);
  const [selectedSymbols, setSelectedSymbols] = useState(new Set());
  const [showGuide, setShowGuide] = useState(false);
  const [pullbacksOnly, setPullbacksOnly] = useState(false);

  const fetchSetups = useCallback(async () => {
    try {
      const data = await setupsApi.getAll();
      setSetups(data.setups || []);
      setLastUpdated(new Date());
      setError(null);
    } catch (err) {
      setError('Failed to load setups. Check your connection and try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSetups();
  }, [fetchSetups]);

  const allSymbols = useMemo(
    () => [...new Set(setups.map(s => s.symbol.toUpperCase()))].sort(),
    [setups]
  );

  const matchesSymbol = useCallback(
    (symbol) => selectedSymbols.size === 0 || selectedSymbols.has(symbol.toUpperCase()),
    [selectedSymbols]
  );

  // ─── Tab renderers ──────────────────────────────────────────────────────────

  const renderSetupsTab = () => {
    const visible = setups.filter(s => {
      if (!matchesSymbol(s.symbol)) return false;
      if (pullbacksOnly && s.type !== 'pullback_pair' && s.type !== 'diamond') return false;
      return true;
    });

    const hasHighConf = visible.some(
      s => s.tier === 'DIAMOND' || s.tier === 'PLATINUM' || s.tier === 'GOLD'
    );

    const sortToggle = (
      <div className="setups-tab-controls">
        <div className="sort-toggle">
          <button
            className={`sort-toggle-btn ${sortMode === 'recency' ? 'active' : ''}`}
            onClick={() => setSortMode('recency')}
          >
            By Recency
          </button>
          <button
            className={`sort-toggle-btn ${sortMode === 'grade' ? 'active' : ''}`}
            onClick={() => setSortMode('grade')}
          >
            By Grade
          </button>
        </div>
        <button
          className={`sort-toggle-btn pullbacks-only-btn ${pullbacksOnly ? 'active' : ''}`}
          onClick={() => setPullbacksOnly(v => !v)}
        >
          Pullbacks Only
        </button>
      </div>
    );

    if (sortMode === 'recency') {
      const sorted = [...visible].sort(
        (a, b) => new Date(b.anchor_timestamp) - new Date(a.anchor_timestamp)
      );
      return (
        <>
          {sortToggle}
          {sorted.length === 0
            ? <EmptyState message="No setups available right now." />
            : (
              <div className="setup-cards-grid">
                {sorted.map((s, i) => (
                  <SetupCard
                    key={`${s.symbol}-${s.type}-${s.timeframe || ''}-${i}`}
                    setup={s}
                    onSelect={setSelectedSetup}
                  />
                ))}
              </div>
            )
          }
        </>
      );
    }

    return (
      <>
        {sortToggle}
        {!hasHighConf && visible.length > 0 && (
          <div className="setup-empty-high-conf">
            <span>🔍</span>
            <p>No Diamond or Platinum setups right now</p>
            <small>Markets are quiet. Gold and lower setups are shown below.</small>
          </div>
        )}

        {visible.length === 0 && <EmptyState message="No setups available right now." />}

        {TIER_ORDER.map(tier => {
          const tierSetups = visible
            .filter(s => s.tier === tier)
            .sort((a, b) => new Date(b.anchor_timestamp) - new Date(a.anchor_timestamp));
          if (tierSetups.length === 0) return null;
          return (
            <div key={tier} className="tier-section">
              <div className="tier-section-header">
                <span>{TIER_EMOJI[tier]} {tier}</span>
                <span className="tier-count">{tierSetups.length}</span>
              </div>
              <div className="setup-cards-grid">
                {tierSetups.map((s, i) => (
                  <SetupCard
                    key={`${s.symbol}-${s.type}-${s.timeframe || ''}-${i}`}
                    setup={s}
                    onSelect={setSelectedSetup}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </>
    );
  };

  const renderChainsTab = () => {
    const chains = setups
      .filter(s => s.type === 'chain' && matchesSymbol(s.symbol))
      .sort((a, b) => new Date(b.anchor_timestamp) - new Date(a.anchor_timestamp));
    if (chains.length === 0) return <EmptyState message="No active chains found." />;
    return (
      <div className="setup-cards-grid">
        {chains.map((s, i) => (
          <ChainCard key={`chain-${s.symbol}-${i}`} setup={s} onSelect={setSelectedSetup} />
        ))}
      </div>
    );
  };

  const renderPullbacksTab = () => {
    // Unpack diamonds into individual pairs; include standalone pullback_pairs
    const allPairs = setups
      .flatMap(s => {
        if (s.type === 'diamond') return s.pullback_pairs || [];
        if (s.type === 'pullback_pair') return [s];
        return [];
      })
      .filter(p => matchesSymbol(p.symbol))
      .sort((a, b) => new Date(b.anchor_timestamp) - new Date(a.anchor_timestamp));

    if (allPairs.length === 0) return <EmptyState message="No pullback pairs found." />;
    return (
      <div className="pullback-raw-list">
        {allPairs.map((p, i) => (
          <PullbackCard
            key={`pb-${p.symbol}-${p.timeframe || ''}-${i}`}
            pair={p}
            onSelect={setSelectedSetup}
          />
        ))}
      </div>
    );
  };

  // ─── Render ─────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="setups-loading">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3 text-muted">Loading setups...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="setups-error">
        <i className="bi bi-exclamation-triangle-fill me-2 text-warning"></i>
        {error}
      </div>
    );
  }

  return (
    <div className="setups-page">
      <div className="setups-header">
        <div className="setups-header-left">
          <h2>💎 Setups</h2>
          {lastUpdated && (
            <small className="text-muted">
              Updated {lastUpdated.toLocaleTimeString()}
            </small>
          )}
        </div>
        <button
          className={`setup-guide-btn ${showGuide ? 'active' : ''}`}
          onClick={() => setShowGuide(v => !v)}
          title="Setup Guide"
        >
          ?
        </button>
      </div>

      <SetupGuide show={showGuide} />

      <div className="setups-filter-bar">
        <SymbolFilter
          allSymbols={allSymbols}
          selectedSymbols={selectedSymbols}
          onChange={setSelectedSymbols}
        />
      </div>

      <Tabs activeKey={activeTab} onSelect={setActiveTab} className="mb-4">
        <Tab eventKey="setups" title="Setups">
          <div className="mt-3">{renderSetupsTab()}</div>
        </Tab>
        <Tab eventKey="chains" title="Chains">
          <div className="mt-3">{renderChainsTab()}</div>
        </Tab>
        <Tab eventKey="pullbacks" title="Pullbacks">
          <div className="mt-3">{renderPullbacksTab()}</div>
        </Tab>
      </Tabs>

      <SetupModal setup={selectedSetup} onHide={() => setSelectedSetup(null)} />
    </div>
  );
};

export default SetupsPage;
